import { updateStoreSettingsOperation } from '../../state/modules/store/operations';
import { createSectionOperation as editCreateSection, createLessonOperation } from '../../state/modules/designCourse/edit/operations';
import { createCourseOperation } from '../../state/modules/designCourse/create/operations';
import { saveLessonTitle, createDefaultCourse, createSection, isMembershipExist } from '../../api/AuthApi';
import { push } from 'connected-react-router';
import Router from 'routes/router';

export const ACTION_TYPES = {
  UPDATE_STORE_SETTING: 'UPDATE_STORE_SETTING',
  UPDATE_STORE_DESIGN: 'UPDATE_STORE_DESIGN',
  PREVIEW_STORE_CHANGE: 'PREVIEW_STORE_CHANGE',
  CREATE_COURSE: 'CREATE_COURSE',
  CREATE_COURSE_OUTLINE: 'CREATE_COURSE_OUTLINE',
  CREATE_MEMBERSHIP: 'CREATE_MEMBERSHIP',
  ADD_LESSON: 'ADD_LESSON',
  UPDATE_LESSON_CONTENT: 'UPDATE_LESSON_CONTENT',
  GENERATE_QUIZ: 'GENERATE_QUIZ',
  GENERATE_COPY: 'GENERATE_COPY',
  IMPROVE_TEXT: 'IMPROVE_TEXT',
  TRANSLATE_CONTENT: 'TRANSLATE_CONTENT',
  SHOW_TUTORIAL: 'SHOW_TUTORIAL',
  OPEN_DOCUMENTATION: 'OPEN_DOCUMENTATION',
  NAVIGATE_TO: 'NAVIGATE_TO'
};

const handleUpdateStoreSetting = async (action, dispatch, getState) => {
  const { field, value } = action.data;

  console.log('[AI Assistant] Updating store setting:', { field, value });

  const settings = {
    [field]: value
  };

  try {
    await dispatch(updateStoreSettingsOperation(settings, { silent: true }));

    return {
      success: true,
      message: `${field.replace('store_', '').replace('_', ' ')} updated to "${value}"`
    };
  } catch (error) {
    console.error('[AI Assistant] Store update failed:', error);
    return {
      success: false,
      error: error.message || 'Failed to update store setting'
    };
  }
};

const handleUpdateStoreDesign = async (action, dispatch, getState) => {
  const { designField, value } = action.data;

  const settings = {
    [designField]: value
  };

  await dispatch(updateStoreSettingsOperation(settings, { silent: true }));

  return {
    success: true,
    message: 'Store design updated successfully'
  };
};

const handlePreviewStoreChange = async (action, dispatch, getState) => {
  return {
    success: true,
    message: 'Preview ready',
    preview: action.data
  };
};

const handleCreateCourse = async (action, dispatch, getState) => {
  const { title, description, sections } = action.data;

  if (!title) {
    return { success: false, error: 'Course title is required' };
  }

  try {
    const { data: { id } } = await createDefaultCourse();

    const courseParams = {
      name: title,
      subtitle: description || '',
    };

    await dispatch(createCourseOperation(courseParams, 'course-details'));

    if (sections && sections.length > 0) {
      for (const section of sections) {
        const sectionName = typeof section === 'string' ? section : (section.title || section.name || section);
        await createSection(id, { name: sectionName });
      }

      dispatch(push(Router.route('ADMIN_COURSES_EDIT').getCompiledPath({ id })));

      return {
        success: true,
        message: `Course "${title}" created with ${sections.length} sections! Opening course editor...`,
        courseId: id
      };
    }

    dispatch(push(Router.route('ADMIN_COURSES_EDIT').getCompiledPath({ id })));

    return {
      success: true,
      message: `Course "${title}" created successfully! Opening course editor...`,
      courseId: id
    };
  } catch (error) {
    console.error('[AI Assistant] Course creation failed:', error);
    return {
      success: false,
      error: error.message || 'Failed to create course'
    };
  }
};

const handleCreateMembership = async (action, dispatch) => {
  const { title } = action.data;

  try {
    const { data } = await isMembershipExist();
    const exists = data?.exists || data?.membership_exists || data?.id;

    if (exists) {
      dispatch(push('/admin/membership/' + (data.id || data.membership_id) + '/edit'));
      return {
        success: true,
        message: 'You already have a membership. Opening it for editing.'
      };
    }

    dispatch(push('/admin/membership/create'));

    return {
      success: true,
      message: `Opening membership creator${title ? ` for "${title}"` : ''}. You can configure the details on the next page.`
    };
  } catch (error) {
    // If the check fails (e.g. 404 means no membership), proceed with creation
    try {
      dispatch(push('/admin/membership/create'));
      return {
        success: true,
        message: `Opening membership creator${title ? ` for "${title}"` : ''}. You can configure the details on the next page.`
      };
    } catch (navError) {
      return {
        success: false,
        error: navError.message || 'Failed to navigate to membership creator'
      };
    }
  }
};

const handleCreateCourseOutline = async (action, dispatch, getState) => {
  const { courseId, sections } = action.data;

  if (!courseId || !sections || !sections.length) {
    return { success: false, error: 'Course ID and sections are required' };
  }

  const createdSections = [];

  for (const section of sections) {
    await dispatch(editCreateSection(courseId, section.title));
    createdSections.push(section.title);
  }

  return {
    success: true,
    message: `Created ${createdSections.length} sections: ${createdSections.join(', ')}`
  };
};

const handleAddLesson = async (action, dispatch, getState) => {
  const { courseId, sectionId, lessonFormat = 'text' } = action.data;

  if (!courseId || !sectionId) {
    return { success: false, error: 'Course ID and Section ID are required' };
  }

  await dispatch(createLessonOperation(courseId, sectionId, lessonFormat));

  return {
    success: true,
    message: `Lesson added to section successfully`
  };
};

const handleUpdateLessonContent = async (action, dispatch, getState) => {
  const { courseId, sectionId, lessonId, content } = action.data;

  if (!courseId || !sectionId || !lessonId) {
    return { success: false, error: 'Course ID, Section ID, and Lesson ID are required' };
  }

  await saveLessonTitle(courseId, sectionId, lessonId, content);

  return {
    success: true,
    message: 'Lesson content updated successfully'
  };
};

const handleGenerateQuiz = async (action, dispatch, getState) => {
  const { topic, questionCount = 5 } = action.data;

  return {
    success: true,
    message: `Quiz outline generated with ${questionCount} questions about "${topic}"`,
    data: action.data
  };
};

const handleGenerateCopy = async (action, dispatch, getState) => {
  const { contentType, context } = action.data;

  return {
    success: true,
    message: `Generated ${contentType} copy`,
    data: action.data
  };
};

const handleImproveText = async (action, dispatch, getState) => {
  const { text, style } = action.data;

  return {
    success: true,
    message: `Text improved with ${style || 'default'} style`,
    data: action.data
  };
};

const handleTranslateContent = async (action, dispatch, getState) => {
  const { text, targetLanguage } = action.data;

  return {
    success: true,
    message: `Content translated to ${targetLanguage}`,
    data: action.data
  };
};

const handleShowTutorial = async (action, dispatch, getState) => {
  const { tutorialId } = action.data;

  return {
    success: true,
    message: `Opening tutorial: ${tutorialId}`
  };
};

const handleOpenDocumentation = async (action, dispatch, getState) => {
  const { docUrl } = action.data;

  window.open(docUrl, '_blank');

  return {
    success: true,
    message: 'Documentation opened in new tab'
  };
};

const handleNavigateTo = async (action, dispatch, getState) => {
  const { route, routeName, params } = action.data;

  try {
    if (routeName && Router.route(routeName)) {
      const path = Router.route(routeName).getCompiledPath(params || {});
      dispatch(push(path));
      return {
        success: true,
        message: `Navigating to ${action.label || routeName}`
      };
    }

    if (route) {
      dispatch(push(route));
      return {
        success: true,
        message: `Navigating to ${action.label || route}`
      };
    }

    return {
      success: false,
      error: 'No valid route provided'
    };
  } catch (error) {
    return {
      success: false,
      error: `Navigation failed: ${error.message}`
    };
  }
};

const actionHandlers = {
  [ACTION_TYPES.UPDATE_STORE_SETTING]: handleUpdateStoreSetting,
  [ACTION_TYPES.UPDATE_STORE_DESIGN]: handleUpdateStoreDesign,
  [ACTION_TYPES.PREVIEW_STORE_CHANGE]: handlePreviewStoreChange,
  [ACTION_TYPES.CREATE_COURSE]: handleCreateCourse,
  [ACTION_TYPES.CREATE_MEMBERSHIP]: handleCreateMembership,
  [ACTION_TYPES.CREATE_COURSE_OUTLINE]: handleCreateCourseOutline,
  [ACTION_TYPES.ADD_LESSON]: handleAddLesson,
  [ACTION_TYPES.UPDATE_LESSON_CONTENT]: handleUpdateLessonContent,
  [ACTION_TYPES.GENERATE_QUIZ]: handleGenerateQuiz,
  [ACTION_TYPES.GENERATE_COPY]: handleGenerateCopy,
  [ACTION_TYPES.IMPROVE_TEXT]: handleImproveText,
  [ACTION_TYPES.TRANSLATE_CONTENT]: handleTranslateContent,
  [ACTION_TYPES.SHOW_TUTORIAL]: handleShowTutorial,
  [ACTION_TYPES.OPEN_DOCUMENTATION]: handleOpenDocumentation,
  [ACTION_TYPES.NAVIGATE_TO]: handleNavigateTo
};

export const executeActionHandler = async (action, dispatch, getState) => {
  const handler = actionHandlers[action.type];

  if (!handler) {
    return {
      success: false,
      error: `Unknown action type: ${action.type}`
    };
  }

  try {
    return await handler(action, dispatch, getState);
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Action execution failed'
    };
  }
};
